import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useApp } from "@/lib/context";

interface MaintenanceStatus {
  enabled: boolean;
  enabledAt: string | null;
  enabledBy: number | null;
}

export function useMaintenanceMode() {
  const { user } = useApp();

  const { data, isLoading, isFetching, error } = useQuery<{
    success: boolean;
    maintenanceMode: MaintenanceStatus;
  }>({
    queryKey: ["/api/admin/maintenance-status"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/admin/maintenance-status");
      return response;
    },
    // Refresh every 30 seconds to catch changes
    refetchInterval: 30000,
    // Always fetch, even if user is not logged in
    enabled: true,
    retry: 3,
    // Keep previous data while refetching to prevent loading flash
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const isMaintenanceMode = data?.maintenanceMode?.enabled || false;

  // Admin users bypass maintenance mode
  const shouldShowMaintenance = isMaintenanceMode && !user?.isAdmin;

  // Only show loading on initial load, not on background refetches
  const isInitialLoading = isLoading && !data;

  return {
    isMaintenanceMode,
    shouldShowMaintenance,
    maintenanceData: data?.maintenanceMode,
    isLoading: isInitialLoading,
    isFetching,
    error,
  };
}
